import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../../components/UI/Layouts";
import Content from "../../components/UI/Layouts/Content";
import { Media } from "../../types";
import PageHeader from "../../components/common/PageHeader";
import { useTranslation } from "react-i18next";
import { useStores } from "../../hooks/useStores";
import { VirtualizedTable } from "../../components/common/VirtualizedTable";
import { Link } from "react-router-dom";
import { Breadcrumb, BreadcrumbItem } from "../../components/UI/Breadcrumb";
import { observer } from "mobx-react";
import EmptySpace from "../../components/common/EmptySpace";
import Loading from "../../components/common/Loading";
import UploadMediaModal from "../../components/ScreenMediaList/UploadMediaModal";
import ReactPaginate from "react-paginate";
import { VIMEO_MEDIA_LIST_LIMIT } from "../../config/const";

const MediaList: React.FC = observer(() => {
  const [page, setPage] = useState(1);
  const { programStore, userStore } = useStores();
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalHandler = () => {
    setIsModalOpen((open) => !open);
  };

  const fetchMediaList = useCallback(async () => {
    await programStore.fetchMediaList(undefined, page, VIMEO_MEDIA_LIST_LIMIT);
  }, [page, programStore]);

  const handleUploadCompleted = useCallback(() => {
    modalHandler();
    fetchMediaList();
  }, [fetchMediaList]);

  const columns = useMemo(
    () => [
      {
        width: 80,
        flexGrow: 2,
        label: t("screens.mediaList.column.title"),
        dataKey: "name",
        sorter: (a: { name: string }, b: { name: any }) =>
          a.name.localeCompare(b.name),
        render: (name: string, media: Media) => {
          return media.programId ? (
            <Link to={"/program/" + media.programId}>{name}</Link>
          ) : (
            name
          );
        },
      },
    ],
    [t]
  );

  useEffect(() => {
    fetchMediaList();
  }, []);

  return (
    <Layout hideFooter>
      <Content borderBottomWidth="1px" align="top">
        <Breadcrumb my={"20px"}>
          <BreadcrumbItem path="/">
            {t("screens.mediaList.breadcrumb.home")}
          </BreadcrumbItem>
          <BreadcrumbItem active>
            {t("screens.mediaList.breadcrumb.media")}
          </BreadcrumbItem>
        </Breadcrumb>

        <PageHeader
          title={t("screens.mediaList.pageHeader.title")}
          buttonEnable={true}
          buttonText={t("screens.mediaList.pageHeader.buttonText")}
          handleButton={modalHandler}
        />
      </Content>
      {programStore.isLoading ? (
        <Loading />
      ) : !programStore.isLoading && programStore.mediaList.length > 0 ? (
        <Content fullHeight align="top" mt={[null, null, null, "0px"]}>
          <div
            style={{
              flex: "1 1 auto",
              width: "100%",
              height: `${(programStore.mediaList.length + 1) * 50}px`,
              display: "flex",
              marginTop: "20px",
            }}
          >
            <VirtualizedTable
              rowCount={programStore.mediaList.length}
              rowGetter={({ index }) => programStore.mediaList[index]}
              columns={columns}
              rowStyle={(row) =>
                row.index % 2 === 0 ? { background: "#EDF1FB" } : {}
              }
            />
          </div>
          <ReactPaginate
            breakLabel="..."
            nextLabel=">"
            onPageChange={(selectedItem) => {
              setPage(selectedItem.selected + 1);
              fetchMediaList();
            }}
            forcePage={page - 1}
            pageCount={Math.floor(
              userStore.vimeoUser.numberOfVideos / VIMEO_MEDIA_LIST_LIMIT
            )}
            previousLabel="<"
            renderOnZeroPageCount={() => null}
            className={"pagination-container"}
            pageClassName={"pagination-page"}
            pageLinkClassName={"pagination-page__link"}
            nextClassName={"pagination-next"}
            nextLinkClassName={"pagination-next__link"}
            previousClassName={"pagination-previous"}
            previousLinkClassName={"pagination-previous__link"}
          />
        </Content>
      ) : (
        <EmptySpace
          title={t("screens.mediaList.emptyData")}
          buttonEnable={true}
          buttonText={t("screens.mediaList.pageHeader.buttonText")}
          handleButton={modalHandler}
        />
      )}

      <UploadMediaModal
        isOpen={isModalOpen}
        onClose={modalHandler}
        onUploadCompleted={handleUploadCompleted}
      />
    </Layout>
  );
});

export default MediaList;
